import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { ImageUpload } from "@/components/ImageUpload";
import { AdRenderer } from "@/components/ads/AdRenderer";
import { Megaphone, Check, Store, Tag, Calendar, Globe, Phone, MessageSquare, Eye, Layers, MapPin, BarChart2, Edit3, MousePointer, TrendingUp, Lock } from "lucide-react";

const CTA_TYPES = [
  { key: "url", label: "Website", icon: Globe },
  { key: "phone", label: "Call", icon: Phone },
  { key: "sms", label: "Text (SMS)", icon: MessageSquare },
  { key: "store", label: "My store", icon: Store },
  { key: "deal", label: "My deal", icon: Tag },
  { key: "event", label: "My event", icon: Calendar },
];
const DURATIONS = [1, 2, 4, 8, 12];
const STORAGE_KEY = "pending_ad_id";
// Sliding scale (mirrors the server): the weekly rate covers all feeds; longer books cheaper.
function weekFactor(w: number) { return w >= 12 ? 0.75 : w >= 8 ? 0.80 : w >= 4 ? 0.85 : w >= 2 ? 0.92 : 1; }

export default function CreateAd() {
  useAuth();
  const token = localStorage.getItem("ayrshire_token");
  const { toast } = useToast();
  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [businessName, setBusinessName] = useState("");
  const [headline, setHeadline] = useState("");
  const [subtext, setSubtext] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [ctaText, setCtaText] = useState("Learn more");
  const [ctaType, setCtaType] = useState("url");
  const [ctaValue, setCtaValue] = useState("");
  const [durationWeeks, setDurationWeeks] = useState(2);
  const [ratePerWeek, setRatePerWeek] = useState(5000);
  const [pricing, setPricing] = useState<{ weeks: number; pricePence: number }[]>([]);
  const [targets, setTargets] = useState<{ stores: { id: number; name: string }[]; deals: { id: number; name: string }[]; events: { id: number; name: string }[] }>({ stores: [], deals: [], events: [] });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch("/api/ads/pricing").then(r => r.ok ? r.json() : null).then(d => { if (d?.ratePerWeek) setRatePerWeek(d.ratePerWeek); if (Array.isArray(d?.durations)) setPricing(d.durations); }).catch(() => {});
    fetch("/api/ads/my-targets", { headers: authHeaders }).then(r => r.ok ? r.json() : null).then(d => { if (d) setTargets({ stores: d.stores ?? [], deals: d.deals ?? [], events: d.events ?? [] }); }).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    if (params.get("ad_paid") === "1") {
      const sessionId = params.get("session_id");
      const adId = sessionStorage.getItem(STORAGE_KEY);
      window.history.replaceState({}, "", window.location.pathname);
      if (adId && sessionId) {
        fetch(`/api/ads/${adId}/verify`, { method: "POST", headers: authHeaders, body: JSON.stringify({ sessionId }) })
          .then(r => r.json()).then(d => { sessionStorage.removeItem(STORAGE_KEY); if (d.status === "pending_review") setDone(true); else toast({ title: "Payment not confirmed", variant: "destructive" }); })
          .catch(() => toast({ title: "Could not confirm payment", variant: "destructive" }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const priceFor = (w: number) => pricing.find(p => p.weeks === w)?.pricePence ?? Math.round(ratePerWeek * w * weekFactor(w));
  const price = priceFor(durationWeeks);
  const base1wk = priceFor(1);
  const ready = Boolean(headline.trim() && ctaValue.trim());

  async function pay() {
    if (!ready) return;
    setSubmitting(true);
    try {
      const draftRes = await fetch("/api/ads", { method: "POST", headers: authHeaders, body: JSON.stringify({ businessName, headline, subtext, imageUrl, ctaText, ctaType, ctaValue, placements: ["food", "deals", "events", "news"], towns: [], durationWeeks }) });
      const draft = await draftRes.json();
      if (!draftRes.ok) throw new Error(draft.error || "Failed");
      sessionStorage.setItem(STORAGE_KEY, String(draft.adId));
      const coRes = await fetch(`/api/ads/${draft.adId}/checkout`, { method: "POST", headers: authHeaders, body: JSON.stringify({ successUrl: `${window.location.origin}${window.location.pathname}`, cancelUrl: window.location.href }) });
      const co = await coRes.json();
      if (!coRes.ok || !co.url) throw new Error(co.error || "Checkout failed");
      window.location.href = co.url;
    } catch (e) { toast({ title: e instanceof Error ? e.message : "Failed", variant: "destructive" }); setSubmitting(false); }
  }

  if (done) return (
    <div className="max-w-lg mx-auto p-6 text-center">
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto"><Check className="text-primary" size={32} /></div>
      <h1 className="text-2xl font-bold mt-4">You're all set - thank you!</h1>
      <p className="text-gray-600 mt-2">Payment received. Our team will give your advert a quick once-over and it'll go live in the app feeds, usually within a few hours.</p>
      <a href="/advertise" className="inline-block mt-5 bg-primary text-white px-5 py-2.5 rounded-xl font-semibold">Done</a>
    </div>
  );

  const list = ctaType === "store" ? targets.stores : ctaType === "deal" ? targets.deals : ctaType === "event" ? targets.events : null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero header */}
      <div className="bg-primary text-white">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="flex items-center gap-2 text-white/80 text-sm font-medium"><Megaphone size={16} /> Advertise on Ayrshire Hub</div>
          <h1 className="text-3xl font-extrabold mt-1">Get seen by thousands of locals</h1>
          <p className="text-white/85 mt-1 max-w-xl">Your advert sits right inside the feeds people already browse - food, deals, events and news. Build it below, see exactly how it looks, then pay by card. Live after a quick review.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 grid lg:grid-cols-[1fr_360px] gap-6 items-start">
        {/* FORM */}
        <div className="space-y-5">
          {/* Your advert */}
          <Section icon={Edit3} title="Your advert">
            <div>
              <FieldLabel>Advert image <span className="text-gray-400 font-normal">· optional, auto-cropped to fit</span></FieldLabel>
              <ImageUpload value={imageUrl} onChange={setImageUrl} />
            </div>
            <Field label="Headline" required value={headline} onChange={setHeadline} max={120} placeholder="20% off this weekend" />
            <Field label="Sub-text" value={subtext} onChange={setSubtext} max={240} textarea placeholder="A little more detail about your offer" />
            <Field label="Business name" value={businessName} onChange={setBusinessName} max={60} placeholder="Shown as a small label above your headline" />
          </Section>

          {/* The button */}
          <Section icon={MousePointer} title="The button">
            <Field label="Button text" value={ctaText} onChange={setCtaText} max={30} placeholder="Learn more" />
            <div>
              <FieldLabel>Where it takes people</FieldLabel>
              <div className="flex flex-wrap gap-2">
                {CTA_TYPES.map(c => {
                  const on = ctaType === c.key;
                  return (
                    <button key={c.key} type="button" onClick={() => { setCtaType(c.key); setCtaValue(""); }}
                      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${on ? "bg-primary border-primary text-white" : "bg-white border-slate-200 text-gray-700 hover:border-primary/40"}`}>
                      <c.icon size={15} /> {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {list ? (
              list.length === 0 ? (
                <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                  <span>You don't have any {ctaType}s yet. Add one first, or point the button at your website or phone instead.</span>
                </div>
              ) : (
                <div>
                  <FieldLabel>Choose your {ctaType} <span className="text-red-500">*</span></FieldLabel>
                  <select value={ctaValue} onChange={e => setCtaValue(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none">
                    <option value="">Select a {ctaType}...</option>
                    {list.map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
                  </select>
                </div>
              )
            ) : (
              <Field label={ctaType === "url" ? "Website address" : "Phone number"} required value={ctaValue} onChange={setCtaValue} placeholder={ctaType === "url" ? "https://yourbusiness.co.uk" : "01292 000000"} />
            )}
          </Section>

          {/* Reach & duration */}
          <Section icon={TrendingUp} title="Reach & duration">
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
              <FeatureRow icon={Layers} title="Shows in every feed" sub="Food, deals, events and news" />
              <FeatureRow icon={MapPin} title="Right across Ayrshire" sub="Seen by locals everywhere the Hub reaches" />
              <FeatureRow icon={BarChart2} title="Track your results" sub="See views and taps in My Adverts" />
            </div>
            <div>
              <FieldLabel>How long? <span className="text-gray-400 font-normal">cheaper the longer you run it</span></FieldLabel>
              <div className="grid grid-cols-5 gap-2">
                {DURATIONS.map(w => {
                  const wk = priceFor(w);
                  const off = base1wk > 0 ? Math.round((1 - (wk / w) / base1wk) * 100) : 0;
                  const on = durationWeeks === w;
                  return (
                    <button key={w} type="button" onClick={() => setDurationWeeks(w)}
                      className={`relative rounded-xl border-2 py-3 flex flex-col items-center transition ${on ? "border-primary bg-primary/5" : "border-slate-200 bg-white hover:border-primary/40"}`}>
                      {w === 4 && <span className="absolute -top-2 bg-primary text-white text-[9px] font-bold px-2 py-0.5 rounded-full">POPULAR</span>}
                      <span className={`text-xs font-semibold ${on ? "text-primary" : "text-gray-700"}`}>{w} wk{w > 1 ? "s" : ""}</span>
                      <span className={`text-lg font-extrabold ${on ? "text-primary" : "text-gray-900"}`}>£{(wk / 100).toFixed(0)}</span>
                      <span className="text-[10px] font-bold text-green-600 h-3">{off > 0 ? `Save ${off}%` : ""}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </Section>
        </div>

        {/* STICKY PREVIEW + PAY */}
        <div className="lg:sticky lg:top-6 space-y-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-primary mb-3"><Eye size={13} /> Live preview <span className="ml-auto text-gray-400 font-medium normal-case tracking-normal">Exactly how it appears</span></div>
            <AdRenderer ad={{
              id: 0, format: imageUrl ? "imageText" : "textOnly",
              businessName: businessName || null, headline: headline || "Your headline here",
              subtext: subtext || "A short line about your offer",
              imageUrl: imageUrl || null, ctaText: ctaText || "Learn more", aspectRatio: "16/9",
            }} />
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-xs text-gray-500">{durationWeeks} week{durationWeeks > 1 ? "s" : ""} · all feeds</div>
                <div className="text-3xl font-extrabold text-gray-900">£{(price / 100).toFixed(2)}</div>
              </div>
              <div className="text-xs text-gray-400 text-right">one-off<br />card payment</div>
            </div>
            <button onClick={pay} disabled={submitting || !ready} className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-xl font-bold disabled:opacity-50 hover:brightness-110 transition">
              <Lock size={15} /> {submitting ? "Opening secure checkout..." : "Pay & submit"}
            </button>
            {!ready && <p className="text-[11px] text-gray-400 text-center mt-2">Add a headline and finish the button to continue</p>}
            <p className="text-[11px] text-gray-400 text-center mt-2">Secure card payment via Stripe. Live after a quick review.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }: { icon: React.ComponentType<{ size?: number; className?: string }>; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center"><Icon size={16} className="text-primary" /></div>
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
      </div>
      {children}
    </div>
  );
}
function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div className="text-sm font-semibold text-gray-700 mb-1.5">{children}</div>;
}
function FeatureRow({ icon: Icon, title, sub }: { icon: React.ComponentType<{ size?: number; className?: string }>; title: string; sub: string }) {
  return (
    <div className="flex items-center gap-3 p-3">
      <Icon size={17} className="text-primary shrink-0" />
      <div className="flex-1"><div className="text-sm font-semibold text-gray-900">{title}</div><div className="text-xs text-gray-500">{sub}</div></div>
      <Check size={16} className="text-primary" />
    </div>
  );
}
function Field({ label, required, value, onChange, max, textarea, placeholder }: { label: string; required?: boolean; value: string; onChange: (s: string) => void; max?: number; textarea?: boolean; placeholder?: string }) {
  const cls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition";
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-sm font-semibold text-gray-700">{label}{required && <span className="text-red-500"> *</span>}</div>
        {max && <div className="text-[11px] text-gray-400">{value.length}/{max}</div>}
      </div>
      {textarea
        ? <textarea value={value} onChange={e => onChange(max ? e.target.value.slice(0, max) : e.target.value)} placeholder={placeholder} className={`${cls} h-20 resize-none`} />
        : <input value={value} onChange={e => onChange(max ? e.target.value.slice(0, max) : e.target.value)} placeholder={placeholder} className={cls} />}
    </div>
  );
}
